import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadCloud } from 'lucide-react';
import { useOnboardingRedirect } from '../hooks/useOnboardingRedirect';
import { shouldSkipResumeUpload } from '../utils/onboardingRouter';
import * as onboardingApi from '../services/onboardingApi';
import { useFlowProtection, setFlowData } from '../../hooks/useFlowProtection';

const BackgroundGrid = () => (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F8F9FB]">
        {/* Ellipse 1: Main Gradient Background */}
        <div
            className="absolute"
            style={{
                width: '2618.47px',
                height: '1939.34px',
                left: '-448.14px',
                top: '-919.31px',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                transform: 'rotate(26.84deg)',
                transformOrigin: 'top left',
            }}
        />

        {/* Ellipse 2827: Blurred Overlay */}
        <div
            className="absolute hidden md:block"
            style={{
                width: '640px',
                height: '458px',
                left: '50%',
                top: '282px',
                transform: 'translateX(-50%) matrix(1, 0, 0, -1, 0, 0)',
                background: 'rgba(255, 255, 255, 0.4)',
                filter: 'blur(93.2px)',
                borderRadius: '53px',
            }}
        />

        {/* Grid Lines */}
        <div className="absolute inset-0 flex justify-center pointer-events-none">
            <div className="w-full h-full max-w-[1920px] relative">
                {/* Vertical Lines */}
                {[...Array(12)].map((_, i) => (
                    <div
                        key={`v-${i}`}
                        className="absolute top-0 bottom-0 border-l border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ left: `${105 + (i * 120)}px` }}
                    />
                ))}

                {/* Horizontal Lines */}
                {[121, 300, 449, 627, 700, 872, 913, 1031].map((top, i) => (
                    <div
                        key={`h-${i}`}
                        className="absolute left-0 right-0 border-t border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ top: `${top}px` }}
                    />
                ))}
            </div>
        </div>
    </div>
);

const Stepper = ({ currentStep }: { currentStep: string }) => {
    const steps = [
        { label: 'Upload your resume', id: 'upload' },
        { label: 'Resume Review & Edit', id: 'review' },
        { label: 'Career Goals & Aspirations', id: 'goals' }
    ];

    const getStatus = (stepId: string) => {
        if (currentStep === 'upload' && stepId === 'upload') return 'active';
        return 'upcoming';
    };

    return (
        <div className="flex items-center justify-center w-full max-w-3xl mx-auto mb-12 relative z-10 px-4">
            {steps.map((step, idx) => {
                const status = getStatus(step.id);
                const isActive = status === 'active';

                return (
                    <div key={idx} className="flex flex-1 items-center last:flex-none">
                        <div className="flex flex-col items-center relative">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center border-[3px] z-10 transition-colors duration-300 ${
                                isActive
                                    ? 'bg-[#8F9FB8] border-[#CBD5E1]'
                                    : 'bg-white border-[#CBD5E1]'
                            }`}>
                                {isActive && <div className="w-2 h-2 bg-white rounded-full" />}
                            </div>
                            <span className={`absolute top-8 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                                isActive ? 'text-[#015FA6] font-bold' : 'text-[#015FA6]/70'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                        {idx !== steps.length - 1 && (
                            <div className={`flex-1 h-[2px] mx-2 transition-colors duration-300 bg-[#CBD5E1]`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const ResumeUpload: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Flow protection - requires coming from welcome/intro or login redirect
    const { isValidAccess } = useFlowProtection({
        storageKey: 'fromWelcomeIntro',
        allowIncompleteProfile: true,
        fallbackPath: '/candidate/dashboard'
    });

    // Redirect users who have already completed onboarding
    useOnboardingRedirect();

    // Check if user should skip resume upload (resume already parsed)
    useEffect(() => {
        const checkIfShouldSkip = async () => {
            const userDataString = localStorage.getItem('authUser');
            if (userDataString) {
                try {
                    const userData = JSON.parse(userDataString);
                    const supabaseUserId = userData.id || userData.user_id;

                    if (supabaseUserId) {
                        console.log('[ResumeUpload] Checking if should skip upload using /me endpoint');

                        // Check if resume is already parsed (uses JWT auth)
                        const skip = await shouldSkipResumeUpload();

                        if (skip) {
                            console.log('[ResumeUpload] Resume already parsed, checking next steps');

                            // Get full onboarding status to determine next page (uses JWT auth)
                            const statusResponse = await onboardingApi.getCurrentOnboardingStatus();
                            const status: onboardingApi.OnboardingStatus = (statusResponse.payload || statusResponse) as unknown as onboardingApi.OnboardingStatus;

                            // Follow sequential flow: Review → Goals → Questions
                            if (!status.hasPreferences) {
                                // Haven't set preferences yet → go to review page (start of sequential flow)
                                console.log('[ResumeUpload] No preferences set → profile-review');
                                navigate('/welcome/profile-review', { replace: true });
                            } else if (status.hasMissingQuestions && status.pendingQuestionsCount > 0) {
                                // Preferences set, has pending questions → onboarding chat
                                console.log('[ResumeUpload] Has pending questions → onboarding-chat');
                                navigate('/welcome/onboarding-chat', { replace: true });
                            } else {
                                // All done → should mark complete or go to chat to handle it
                                console.log('[ResumeUpload] All done → onboarding-chat');
                                navigate('/welcome/onboarding-chat', { replace: true });
                            }
                        }
                    }
                } catch (error) {
                    console.error('[ResumeUpload] Error checking skip status:', error);
                }
            }
        };

        checkIfShouldSkip();
    }, [navigate]);

    const handleFileChange = (file: File | null) => {
        if (file && file.type === 'application/pdf') {
            setResumeFile(file);
        } else {
            alert('Please upload a PDF file only');
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setIsDragging(true);
        } else if (e.type === "dragleave") {
            setIsDragging(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleNext = () => {
        if (resumeFile) {
            // Set flow data for next page
            setFlowData('fromResumeUpload', { resumeFile: resumeFile.name });
            navigate('/welcome/resume-confirmation');
        }
    };

    // Don't render until flow access is validated
    if (isValidAccess === null) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col overflow-hidden">
            <BackgroundGrid />

            {/* Header */}
            <header className="relative z-10 pt-8 pb-4 text-center">
                <div className="mb-6">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        style={{
                            width: '100px',
                            height: '46px',
                            objectFit: 'contain',
                            margin: '0 auto'
                        }}
                    />
                </div>
                <Stepper currentStep="upload" />
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-1 px-4 md:px-8 py-4 max-w-[1400px] mx-auto w-full flex flex-col">
                <div className="flex flex-col items-center w-full max-w-4xl mx-auto animate-fade-in px-4">
                    {/* 4xl Header Typography */}
                    <h2 className="text-[#015FA6] font-bold text-[36px] md:text-[48px] leading-[100%] mb-4 text-center">
                        We'll need your resume to move forward.
                    </h2>
                    {/* Subtitle Typography */}
                    <p className="text-[#015FA6] font-extralight text-[20px] md:text-[24px] leading-[150%] mb-12 text-center max-w-3xl">
                        Upload your latest resume (PDF only) so we can tailor your opportunities perfectly.
                    </p>

                    <div
                        className={`w-full max-w-xl bg-white rounded-2xl shadow-xl transition-all duration-300 border-2 ${
                            isDragging ? 'border-[#015FA6] scale-[1.02]' : 'border-transparent hover:border-[#015FA6]/30'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        <div className="p-12 flex flex-col items-center justify-center text-center">
                            <div className="w-20 h-20 mb-6 text-[#015FA6]">
                                <UploadCloud strokeWidth={1.5} size={80} />
                            </div>

                            <h3 className="text-xl font-semibold text-[#015FA6] mb-2">
                                {resumeFile ? resumeFile.name : 'Drag & drop your resume'}
                            </h3>
                            <p className="text-sm text-[#015FA6]/60 mb-8">
                                Drag and drop a file or{' '}
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-[#015FA6] font-bold underline decoration-2 underline-offset-2 hover:text-[#014f8c]"
                                >
                                    browse file
                                </button>
                            </p>

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                                accept=".pdf,.doc,.docx"
                                className="hidden"
                            />

                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full max-w-xs py-3 px-6 bg-[#015FA6] text-white rounded-lg font-bold hover:bg-[#014f8c] transition-colors shadow-lg flex items-center justify-center gap-2"
                            >
                                <UploadCloud size={20} />
                                Upload your CV
                            </button>
                        </div>
                    </div>

                    {/* Hidden button for visual balance with design, real action is in card */}
                    <div className="w-full flex justify-end mt-12 max-w-4xl opacity-0 pointer-events-none">
                        <button className="px-8 py-3 bg-[#015FA6] text-white rounded-lg">Next</button>
                    </div>
                </div>
            </main>

            {/* Footer with Next Button */}
            {resumeFile && (
                <div className="fixed bottom-8 right-8 z-20">
                    <button
                        onClick={handleNext}
                        className="px-12 py-3 bg-[#015FA6] text-white rounded-lg font-bold hover:bg-[#014f8c] transition-all shadow-lg shadow-blue-900/20"
                    >
                        Next
                    </button>
                </div>
            )}

            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default ResumeUpload;
